import { useMutation } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import { useEffect, useRef, useState } from 'react';

import { buddyStore } from 'global/Buddy/BuddyStore';
import {
	clearAllHistory as clearAllHistoryDb,
	deleteConversation as deleteConversationDb,
	groupConversationsByDate,
	loadConversation,
	loadConversations,
	saveMessage,
} from 'lib/buddyChatHistory';
import { DEV_USER_ID, clearSeedData } from 'lib/buddyChatHistory/seed';
import { formatMsgTime } from 'utils/formatMsgTime';

import { SUGGESTED_QUERIES } from './const';
import { CHAT_MUTATION } from './gql/chat';

const mapMessages = (msgs) =>
	msgs.map((m) => ({
		id: String(m.id),
		role: m.role,
		text: m.text,
		card: m.card ?? null,
		time: formatMsgTime(m.createdAt),
	}));

export function useAssistant() {
	const [activeConversationId, setActiveConversationId] = useState(null);
	const [conversationGroups, setConversationGroups] = useState([]);
	const [inputValue, setInputValue] = useState('');
	const messagesContainerRef = useRef(null);
	const inputRef = useRef(null);
	const [sendChat] = useMutation(CHAT_MUTATION);

	const { messages, isLoading } = buddyStore;

	useEffect(() => {
		const load = async () => {
			await clearSeedData();

			const convs = await loadConversations({ userId: DEV_USER_ID });

			setConversationGroups(groupConversationsByDate(convs));

			if (convs.length === 0 || activeConversationId) return;

			const latest = convs[0];

			setActiveConversationId(latest.conversationId);

			const msgs = await loadConversation({
				userId: DEV_USER_ID,
				conversationId: latest.conversationId,
			});

			buddyStore.setMessages(mapMessages(msgs));
		};

		load().catch((err) =>
			datadogRum.addError(err, { source: 'assistant', operation: 'loadHistory' })
		);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		const el = messagesContainerRef.current;

		if (el) el.scrollTop = el.scrollHeight;
	}, [messages.length, isLoading]);

	const handleSelectConversation = async (id) => {
		setActiveConversationId(id);

		const msgs = await loadConversation({ userId: DEV_USER_ID, conversationId: id });

		buddyStore.setMessages(mapMessages(msgs));
	};

	const handleNewConversation = () => {
		buddyStore.clearMessages();
		setActiveConversationId(null);
		inputRef.current?.focus?.();
	};

	const handleInputChange = (e) => setInputValue(e.target.value);

	const handleSend = async (overrideText) => {
		const text = (overrideText !== undefined ? overrideText : inputValue).trim();

		if (!text) return;

		if (overrideText === undefined) setInputValue('');

		const convId = activeConversationId ?? `conv-${Date.now()}`;

		if (!activeConversationId) setActiveConversationId(convId);

		const history = buddyStore.messages
			.filter((m) => m.id !== 'greeting')
			.map((m) => ({ role: m.role.toUpperCase(), text: m.text }));

		history.push({ role: 'USER', text });
		buddyStore.addUserMessage(text);
		buddyStore.setLoading(true);

		const userCreatedAt = Date.now();

		await saveMessage({
			userId: DEV_USER_ID,
			conversationId: convId,
			role: 'user',
			text,
			card: null,
			createdAt: userCreatedAt,
		});

		try {
			const { data } = await sendChat({ variables: { input: { messages: history } } });
			const response = data.chat;
			const card =
				response.crewMembers?.length > 0
					? { type: 'crewMembers', data: response.crewMembers }
					: null;

			buddyStore.addAssistantMessage(response.text, card);
			buddyStore.setLoading(false);

			await saveMessage({
				userId: DEV_USER_ID,
				conversationId: convId,
				role: 'assistant',
				text: response.text,
				card,
				createdAt: Date.now(),
			});

			const convs = await loadConversations({ userId: DEV_USER_ID });

			setConversationGroups(groupConversationsByDate(convs));
		} catch {
			buddyStore.setLoading(false);
		}
	};

	const handleInputKeyDown = (e) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	const handleSuggestChip = (chip) => {
		setInputValue(chip);
		inputRef.current?.focus?.();
	};

	const handleEditAndAsk = (text) => {
		setInputValue(text);
		inputRef.current?.focus?.();
	};

	const handleResend = (text) => handleSend(text);

	const handleDeleteConversation = async (id) => {
		await deleteConversationDb({ userId: DEV_USER_ID, conversationId: id });

		if (activeConversationId === id) {
			buddyStore.clearMessages();
			setActiveConversationId(null);
		}

		setConversationGroups((prev) =>
			prev
				.map((group) => ({
					...group,
					items: group.items.filter((item) => item.conversationId !== id),
				}))
				.filter((group) => group.items.length > 0)
		);
	};

	const handleClearAllHistory = async () => {
		await clearAllHistoryDb({ userId: DEV_USER_ID });
		buddyStore.clearMessages();
		setActiveConversationId(null);
		setConversationGroups([]);
	};

	const messagesWithSuggestions = messages.map((msg, i) => {
		if (i === 0 && msg.role === 'assistant') return { ...msg, suggestions: SUGGESTED_QUERIES };

		return msg;
	});

	return {
		activeConversationId,
		conversationGroups,
		inputValue,
		messagesContainerRef,
		inputRef,
		messages: messagesWithSuggestions,
		isLoading,
		handleSelectConversation,
		handleNewConversation,
		handleInputChange,
		handleSend,
		handleInputKeyDown,
		handleSuggestChip,
		handleEditAndAsk,
		handleResend,
		handleDeleteConversation,
		handleClearAllHistory,
	};
}
